import { lazy, ComponentType } from 'react';
import { attemptSelfHealReload } from './staleTabSelfHeal';
import {
  chunkRecoveryRecentlyActive,
  isChunkClassError,
  markChunkRecoveryActivity,
  noteChunkRecoveryActivity,
} from './chunkRecoveryMark';

// The recovery mark lives in chunkRecoveryMark.ts (no imports, so a loader in
// the chat's thread store can use it without this module's React graph); its
// callers import it from here as they always have.
export { chunkRecoveryRecentlyActive, noteChunkRecoveryActivity };

/**
 * A wrapper around React.lazy that adds retry logic for failed chunk loads.
 * This handles ChunkLoadError which occurs when webpack fails to load a lazy
 * chunk (e.g., due to network issues, navigation during streaming, or a
 * rolling deploy).
 *
 * What retries can and cannot fix: every retry re-requests the SAME chunk
 * URL after a plain delay — there is no cache-busting here (an earlier
 * edition of this file claimed one that never existed). Retries therefore
 * only ride out transient network blips. A PRUNED chunk — deploy.yml deletes
 * hashed /static/js/*.js past retention, after which the CDN serves
 * index.html as 200 text/html — fails identically on every retry; the only
 * fix is reloading fresh HTML, which is delegated to staleTabSelfHeal, the
 * one owner of recovery-reload policy (manifest-gated so offline never
 * reloads, loop-guarded, streaming-lease aware).
 *
 * @param importFn - The dynamic import function (e.g., () => import('./MyComponent'))
 * @param retries - Number of retry attempts (default: 3)
 * @param retryDelay - Delay between retries in ms (default: 1000)
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  retryDelay = 1000
): React.LazyExoticComponent<T> {
  return lazy(() => retryImport(importFn, retries, retryDelay));
}

/** Latch key from the retired reload-once scheme. Never written any more;
 * removed on every successful import so tabs latched by it heal. */
const LEGACY_RELOAD_LATCH_KEY = 'olivia__chunk_reload_attempted';

/**
 * The chunk branch of App.tsx's global unhandledrejection handler, here so a test
 * drives the real rule. A chunk rejection is suppressed only while a loader has
 * marked recovery active (retryImport, or noteChunkRecoveryActivity in chunkRecoveryMark.ts); with
 * none in flight it is a real failure (a bare import()?) and stays loud. Returns
 * true when the rejection was a chunk rejection, suppressed or not.
 */
export function handleChunkRejectionEvent(event: { reason?: any; preventDefault(): void }): boolean {
  const errorMessage: string = event.reason?.message || '';
  const errorName: string = event.reason?.name || '';
  if (
    errorName !== 'ChunkLoadError'
    && !errorMessage.includes('Loading chunk')
    && !errorMessage.includes('ChunkLoadError')
  ) {
    return false;
  }
  if (chunkRecoveryRecentlyActive()) {
    event.preventDefault();
    console.warn('chunk load error suppressed (lazyWithRetry owns recovery):', errorMessage);
  } else {
    console.error('chunk load error with no active lazyWithRetry recovery (bare import()?):', errorMessage);
  }
  return true;
}

/** Our own code-split files: a same-origin /static/js/<name>.chunk.js. main.js is not one. */
const CHUNK_FILE = /^\/static\/js\/[^/]+\.chunk\.js$/;

/**
 * A chunk file that turned out not to be a script (2026-09-25). A pruned chunk is
 * answered with the app's own index.html (200 text/html, the SPA fallback) and the
 * browser runs it: the chunk's own file throws a SyntaxError ("Unexpected token
 * '<'"), reported as an uncaught page error, BEFORE webpack notices the chunk never
 * registered and rejects the load with a ChunkLoadError. That rejection is where
 * ownership is decided (handleChunkRejectionEvent: owned is quiet, unowned is loud),
 * so this SyntaxError is only an early duplicate of the same failure — early enough
 * that no loader can have claimed it yet. Measured on prest0.ai with the chat skin's
 * chunk served as html: one such page error per attempt beside the ChunkLoadError,
 * and route chunks the same. Cancelled here with a warning naming the file; nothing
 * else — not main.js, not another origin's script, not an error that is not a
 * SyntaxError. Returns whether it cancelled.
 */
export function handleChunkScriptErrorEvent(
  event: { error?: unknown; filename?: string; message?: string; preventDefault(): void },
  origin: string = typeof window !== 'undefined' ? window.location.origin : '',
): boolean {
  let file: URL;
  try {
    file = new URL(event.filename || '', origin || undefined);
  } catch {
    return false;
  }
  if (!event.filename || file.origin !== origin || !CHUNK_FILE.test(file.pathname)) return false;
  const isSyntaxError = event.error instanceof SyntaxError || /SyntaxError/.test(event.message || '');
  if (!isSyntaxError) return false;
  event.preventDefault();
  const detail = event.error instanceof Error ? event.error.message : event.message;
  console.warn('chunk file was not a script (its ChunkLoadError decides whether the failure is owned):', file.pathname, detail);
  return true;
}

/**
 * The retry-then-heal policy for ANY dynamic import. Production callers:
 * lazyWithRetry (route components through React.lazy) and useDeferredModule
 * (modules loaded after first paint WITHOUT React.lazy — the chat engine and
 * the deferred routes). Generic over the module's shape because the chat
 * engine exports a named component, not a default one.
 */
export async function retryImport<M>(
  importFn: () => Promise<M>,
  retries: number,
  retryDelay: number
): Promise<M> {
  let lastError: any;
  let chunkClass = false;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (attempt > 0) {
        // Plain wait, then the same URL again — transient blips only (see
        // the header note; a pruned chunk needs the reload path below).
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
      const mod = await importFn();
      try {
        sessionStorage.removeItem(LEGACY_RELOAD_LATCH_KEY);
      } catch {
        /* storage unusable — nothing to heal */
      }
      return mod;
    } catch (error: any) {
      lastError = error;
      chunkClass = isChunkClassError(error);
      // A non-chunk error is the module's own bug: never retried, never
      // reloaded — it goes straight to the error boundary.
      if (!chunkClass) break;
      markChunkRecoveryActivity();
      if (attempt === retries) break;
      console.warn(
        `Chunk load failed (attempt ${attempt + 1}/${retries + 1}), retrying...`,
        error?.message
      );
    }
  }

  if (chunkClass) {
    console.error('Failed to load chunk after retries:', lastError?.message);
    // ALL reload policy lives in staleTabSelfHeal: manifest reachability
    // (offline refuses), timestamped loop guards, per-tab cap, streaming
    // lease. It may resolve true with the reload lease-deferred — the
    // pending lazy promise below keeps the Suspense fallback up meanwhile.
    const healed = await attemptSelfHealReload('chunk-failure');
    if (healed) {
      // Reload committed. Hold the promise so Suspense — not the error
      // boundary — shows while the page tears down; the 10s escape hatch
      // still reaches the boundary if the reload was somehow blocked.
      await new Promise((resolve) => setTimeout(resolve, 10_000));
    }
    markChunkRecoveryActivity();
    try {
      // Marks the error for ErrorBoundary's reload-first chunk card: a
      // React.lazy rejection is cached for the module lifetime, so a
      // boundary reset cannot revive it — only a reload can.
      lastError.__prest0ChunkRecoveryExhausted = true;
    } catch {
      /* frozen/exotic error object — the boundary also matches name/message */
    }
  }

  throw lastError;
}

export default lazyWithRetry;
